import React, {

  useCallback,

  useEffect,

  useMemo,

  useState,
  useRef,

} from 'react';



import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ClinicalNoteVisitNavigator from './ClinicalNoteVisitNavigator.jsx';
import ClinicalNoteNavigationCoordinator from './clinicalNoteNavigationCoordinator.js';



import clinicalNoteService from './clinicalNoteService.js';
import useClinicalSectionStatuses from './useClinicalSectionStatuses.js';
import './ClinicalNoteWorkspace.css';



import ClinicalNoteEvaluationForm from './ClinicalNoteEvaluationForm.jsx';

import ClinicalNoteDailyForm from './ClinicalNoteDailyForm.jsx';

import ClinicalNoteProgressForm from './ClinicalNoteProgressForm.jsx';

import ClinicalNoteDischargeForm from './ClinicalNoteDischargeForm.jsx';



import ClinicalNoteWarningPanel from './ClinicalNoteWarningPanel.jsx';

import ClinicalNoteValidationPanel from './ClinicalNoteValidationPanel.jsx';

import ClinicalNoteWorkflowPanel from './ClinicalNoteWorkflowPanel.jsx';

import ClinicalNoteSignaturePanel from './ClinicalNoteSignaturePanel.jsx';

import ClinicalNoteFinalDocumentPanel from './ClinicalNoteFinalDocumentPanel.jsx';
import ClinicalNotePdfPacketPanel from './ClinicalNotePdfPacketPanel.jsx';



/**

 * Convert backend enum-style values into readable labels.

 *

 * Examples:

 *

 * INITIAL_EVALUATION -> Initial Evaluation

 * PROGRESS_NOTE      -> Progress Note

 * READY_FOR_REVIEW   -> Ready For Review

 */

function formatEnum(value) {

  if (

    value === null ||

    value === undefined ||

    value === ''

  ) {

    return '—';

  }



  return String(value)

    .toLowerCase()

    .split('_')

    .map(

      (part) =>

        part.charAt(0).toUpperCase() +

        part.slice(1)

    )

    .join(' ');

}



/**

 * Format YYYY-MM-DD values without allowing browser timezone

 * conversion to shift the clinical date.

 */

function formatClinicalDate(value) {

  if (!value) {

    return '—';

  }



  const match =

    String(value).match(

      /^(\d{4})-(\d{2})-(\d{2})$/

    );



  if (!match) {

    return String(value);

  }



  const [

    ,

    year,

    month,

    day,

  ] = match;



  return `${month}/${day}/${year}`;

}



/**

 * Resolve the main note object from the complete-detail response.

 *

 * This remains defensive because the frontend should not depend on

 * one wrapper shape when the actual clinical-note object is already

 * recognizable.

 */

function getNoteHeader(detail) {

  if (!detail) {

    return {};

  }



  if (

    detail.note &&

    typeof detail.note === 'object'

  ) {

    return detail.note;

  }



  if (

    detail.clinicalNote &&

    typeof detail.clinicalNote ===

      'object'

  ) {

    return detail.clinicalNote;

  }



  return detail;

}



/**

 * Resolve the note type.

 */

function getNoteType(detail) {

  const note =

    getNoteHeader(detail);



  return (

    note.noteType ||

    note.type ||

    detail?.noteType ||

    null

  );

}



/**

 * Resolve workflow status.

 */

function getNoteStatus(detail) {

  const note =

    getNoteHeader(detail);



  return (

    note.status ||

    detail?.status ||

    null

  );

}



/**

 * Resolve the current optimistic-locking version.

 */

function getEntityVersion(detail) {

  const note =

    getNoteHeader(detail);



  return (

    note.entityVersion ??

    detail?.entityVersion ??

    null

  );

}



/**

 * Determine whether the note has been permanently locked.

 */

function isNoteLocked(detail) {

  const note =

    getNoteHeader(detail);



  return Boolean(

    note.locked ??

    detail?.locked ??

    false

  );

}



/**

 * Merge a freshly loaded ClinicalNoteResponse into the current

 * ClinicalNoteDetailResponse without discarding the other clinical

 * sections already present in the detail response.

 */

function mergeNoteResponse(

  currentDetail,

  noteResponse

) {

  if (

    !currentDetail ||

    !noteResponse

  ) {

    return (

      noteResponse ||

      currentDetail

    );

  }



  if (

    currentDetail.note &&

    typeof currentDetail.note ===

      'object'

  ) {

    return {

      ...currentDetail,



      note: {

        ...currentDetail.note,

        ...noteResponse,

      },



      entityVersion:

        noteResponse.entityVersion ??

        currentDetail.entityVersion,

    };

  }



  if (

    currentDetail.clinicalNote &&

    typeof currentDetail.clinicalNote ===

      'object'

  ) {

    return {

      ...currentDetail,



      clinicalNote: {

        ...currentDetail.clinicalNote,

        ...noteResponse,

      },



      entityVersion:

        noteResponse.entityVersion ??

        currentDetail.entityVersion,

    };

  }



  return {

    ...currentDetail,

    ...noteResponse,

  };

}



/**

 * Human-readable document title.

 */

function getDocumentTitle(noteType) {

  switch (noteType) {

    case 'INITIAL_EVALUATION':

      return 'Initial Evaluation';



    case 'RE_EVALUATION':

      return 'Re-Evaluation';



    case 'DAILY':

      return 'Daily Note';



    case 'PROGRESS_NOTE':

      return 'Progress Note';



    case 'DISCHARGE':

      return 'Discharge Note';



    default:

      return 'Clinical Note';

  }

}



/**

 * Reusable metadata field.

 */

function MetadataItem({

  label,

  value,

  wide = false,

}) {

  return (

    <div

      style={{

        minWidth: 150,

        flex: wide
          ? '1 1 100%'
          : '1 1 150px',

      }}

    >

      <div

        style={{

          fontSize: 12,

          fontWeight: 700,

          textTransform: 'uppercase',

          letterSpacing: '0.04em',

          color: '#666',

          marginBottom: 4,

        }}

      >

        {label}

      </div>



      <div

        style={{

          fontSize: 15,

          color: '#222',

          wordBreak: 'break-word',

        }}

      >

        {value ?? '—'}

      </div>

    </div>

  );

}



const SECTION_CODES_BY_NOTE_TYPE = {
  INITIAL_EVALUATION: [
    'EVALUATION_HISTORY', 'PAIN', 'ROM', 'STRENGTH', 'GOAL',
    'SPECIAL_TEST', 'PALPATION', 'CONTENT', 'PLAN_OF_CARE', 'ATTACHED_DAILY',
  ],
  RE_EVALUATION: [
    'EVALUATION_HISTORY', 'PAIN', 'ROM', 'STRENGTH', 'GOAL',
    'SPECIAL_TEST', 'PALPATION', 'CONTENT', 'PLAN_OF_CARE', 'ATTACHED_DAILY',
  ],
  DAILY: ['PAIN', 'ROM', 'STRENGTH', 'GOAL', 'CONTENT', 'TREATMENT'],
  PROGRESS_NOTE: [
    'PAIN', 'ROM', 'STRENGTH', 'GOAL', 'SPECIAL_TEST',
    'PALPATION', 'CONTENT', 'TREATMENT', 'PLAN_OF_CARE', 'ATTACHED_DAILY',
  ],
  DISCHARGE: ['PAIN', 'ROM', 'STRENGTH', 'GOAL', 'CONTENT', 'TREATMENT'],
};

function calculateClinicalProgress(noteType, statuses) {
  const sectionCodes = SECTION_CODES_BY_NOTE_TYPE[noteType] || [];
  let complete = 0;
  let review = 0;
  let incomplete = 0;
  let checking = 0;


  sectionCodes.forEach((sectionCode) => {
    const level = statuses?.[sectionCode]?.level || 'neutral';

    if (level === 'green') {
      complete += 1;

    } else if (level === 'yellow') {
      review += 1;

    } else if (level === 'red') {
      incomplete += 1;
    } else {
      checking += 1;
    }
  });

  return {
    percent: sectionCodes.length > 0
      ? Math.round((complete / sectionCodes.length) * 100)
      : 0,
    total: sectionCodes.length,
    complete,
    review,
    incomplete,
    checking,
  };
}

const SECTION_STATUS_LABELS = {
  EVALUATION_HISTORY: 'Evaluation History',
  PAIN: 'Pain', ROM: 'Range of Motion', STRENGTH: 'Strength / GMMT',
  GOAL: 'Goals', SPECIAL_TEST: 'Special Tests', PALPATION: 'Palpation',
  CONTENT: 'Clinical Content', TREATMENT: 'Treatment',
  PLAN_OF_CARE: 'Plan of Care', ATTACHED_DAILY: 'Attached Daily Note',
};

function ClinicalProgressCard({ progress, noteType, statuses }) {
  const percent = Math.max(0, Math.min(100, Number(progress?.percent) || 0));

  return (
    <aside className="clinical-note-progress-card" aria-label="Clinical note progress">
      <div
        className="clinical-note-progress-ring"
        style={{
          background: `conic-gradient(#2563eb ${percent * 3.6}deg, #e2e8f0 0deg)`,
        }}
      >
        <div className="clinical-note-progress-ring__inner">
          <strong>{percent}%</strong>
          <span>verified</span>
        </div>
      </div>

      <div className="clinical-note-progress-copy">
        <div className="clinical-note-progress-copy__title">Section Status</div>
        <div className="clinical-note-progress-copy__summary">
          {progress.complete} of {progress.total} sections verified
        </div>
        <div className="clinical-note-progress-copy__legend">
          {progress.review > 0 && <span className="is-review">{progress.review} review</span>}
          {progress.incomplete > 0 && <span className="is-incomplete">{progress.incomplete} incomplete</span>}
          {progress.checking > 0 && <span>{progress.checking} N/A</span>}
          {progress.review === 0 && progress.incomplete === 0 && progress.checking === 0 && (
            <span className="is-complete">All sections verified; run final validation</span>
          )}
        </div>
      </div>
      <div className="clinical-note-progress-sections" aria-label="Individual section statuses">
        {(SECTION_CODES_BY_NOTE_TYPE[noteType] || []).map((code) => {
          const level = statuses?.[code]?.level || 'neutral';
          const label = level === 'green' ? 'Verified'
            : level === 'yellow' ? 'Review'
            : level === 'red' ? 'Incomplete' : 'N/A';
          return (
            <div className="clinical-note-progress-section" key={code}>
              <span className="clinical-note-progress-section__name">
                {SECTION_STATUS_LABELS[code] || code.replace(/_/g, ' ')}
              </span>
              <span className={`clinical-note-progress-section__state is-${level}`}>
                <span className="clinical-note-progress-section__dot" aria-hidden="true" />
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

/**

 * Main shared clinical-note workspace.

 *

 * Used by:

 *

 * - Initial Evaluation

 * - Re-Evaluation

 * - Daily Note

 * - Progress Note

 * - Discharge Note

 *

 * Individual section components remain responsible for loading,

 * editing, autosaving, warning handling, validation, and preserving

 * the optimistic-lock version actually reviewed by the user.

 */

export default function ClinicalNotePage({

  clinicalNoteId:

    clinicalNoteIdProp,

}) {

  const params =

    useParams();

  const navigate = useNavigate();
  const location = useLocation();
  const [selectedNoteId, setSelectedNoteId] = useState(null);



  /*

   * Support either:

   *

   * /clinical-notes/:clinicalNoteId

   *

   * or:

   *

   * <ClinicalNotePage clinicalNoteId={123} />

   */

  const routeNoteId = clinicalNoteIdProp ?? params.clinicalNoteId ?? params.id;
  const clinicalNoteId = selectedNoteId ?? routeNoteId;

  const navigationCoordinator = useMemo(() => new ClinicalNoteNavigationCoordinator({
    waitForPendingMutations: (noteId) => clinicalNoteService.waitForPendingMutations(noteId),
  }), []);

  const [episodeVisits, setEpisodeVisits] = useState([]);
  const [visitsError, setVisitsError] = useState('');
  const [visitNavigationBusy, setVisitNavigationBusy] = useState(false);
  const [typeChangeBusy, setTypeChangeBusy] = useState(false);
  const detailLoadSequenceRef = useRef(0);
  const activeNoteIdRef = useRef(clinicalNoteId);
  activeNoteIdRef.current = clinicalNoteId;

  useEffect(() => {
    setSelectedNoteId(null);
  }, [routeNoteId]);



  
  const {
    statuses: pageSectionStatuses,
    refreshSectionStatuses: refreshPageSectionStatuses,
  } = useClinicalSectionStatuses(clinicalNoteId);

  const [

    detail,

    setDetail,

  ] = useState(null);



  const [

    signature,

    setSignature,

  ] = useState(null);



  const [

    loading,

    setLoading,

  ] = useState(true);



  const [

    refreshing,

    setRefreshing,

  ] = useState(false);



  const [

    pageError,

    setPageError,

  ] = useState('');



  const [

    activeDocumentAction,

    setActiveDocumentAction,

  ] = useState('');



  /*

   * Panels that depend on the overall note state can use this token

   * to reload after another section successfully changes the note.

   *

   * IMPORTANT:

   *

   * The token tells a panel that newer data exists.

   * It does NOT replace the optimistic-lock version already reviewed

   * by an irreversible action.

   */

  const [

    refreshToken,

    setRefreshToken,

  ] = useState(0);



  /*

   * Signature panel accepts the current role directly.

   *

   * It also has its own safe localStorage fallback, but providing the

   * role here keeps the page explicit.

   */

  const userRole =

    useMemo(

      () => {

        if (

          typeof window ===

          'undefined'

        ) {

          return '';

        }



        return (

          window.localStorage

            .getItem('role') ||

          ''

        );

      },

      []

    );



  /**

   * Tell cross-section panels that some successful operation changed

   * the note/warnings/validation state.

   */

  const triggerPanelRefresh =

    useCallback(() => {

      setRefreshToken(

        (current) =>

          current + 1

      );

    }, []);



  /**

   * Load the complete note and safe permanent signature

   * representation.

   */

  const loadClinicalNote =

    useCallback(

      async (

        showMainLoading = true,

        refreshChildren = false

      ) => {

        const requestSequence = ++detailLoadSequenceRef.current;
        const requestedNoteId = clinicalNoteId;
        if (!clinicalNoteId) {

          setPageError(

            'Clinical note ID is missing.'

          );



          setLoading(false);

          return;

        }



        if (showMainLoading) {

          setLoading(true);

        } else {

          setRefreshing(true);

        }



        setPageError('');



        try {

          const [

            detailResponse,

            signatureResponse,

          ] =

            await Promise.all([

              clinicalNoteService

                .getClinicalNoteDetail(

                  clinicalNoteId

                ),



              clinicalNoteService

                .getSignature(

                  clinicalNoteId

                ),

            ]);



          if (requestSequence !== detailLoadSequenceRef.current ||
              String(activeNoteIdRef.current) !== String(requestedNoteId)) return;

          setDetail(

            detailResponse

          );



          setSignature(

            signatureResponse ||

              null

          );



          if (refreshChildren) {

            triggerPanelRefresh();

          }

        } catch (error) {
          if (requestSequence !== detailLoadSequenceRef.current ||
              String(activeNoteIdRef.current) !== String(requestedNoteId)) return;

          setPageError(

            error?.message ||

              'The clinical note could not be loaded.'

          );

        } finally {

          if (showMainLoading) {

            setLoading(false);

          }



          setRefreshing(false);

        }

      },

      [

        clinicalNoteId,

        triggerPanelRefresh,

      ]

    );



  useEffect(() => {
    detailLoadSequenceRef.current += 1;
    setDetail(null);
    setSignature(null);
    setEpisodeVisits([]);
    setVisitsError('');
    void loadClinicalNote(true);
    return () => { detailLoadSequenceRef.current += 1; };
  }, [loadClinicalNote]);

  const clinicalEpisodeId = getNoteHeader(detail)?.clinicalEpisodeId ?? detail?.clinicalEpisodeId;

  useEffect(() => {
    let cancelled = false;
    setEpisodeVisits([]);
    setVisitsError('');
    if (!clinicalEpisodeId || !clinicalNoteId) return undefined;
    clinicalNoteService.getEpisodeVisits(clinicalEpisodeId)
      .then((visits) => {
        if (!cancelled) setEpisodeVisits(visits);
      })
      .catch((error) => {
        if (!cancelled) setVisitsError(error?.message || 'Visit navigation could not be loaded.');
      });
    return () => { cancelled = true; };
  }, [clinicalEpisodeId, clinicalNoteId]);

  /**
   * Finish every registered section autosave before leaving the current note.
   * A failure keeps the current visit and all editors mounted.
   */
  const prepareCurrentNoteToLeave = useCallback(async (sourceId) => {
    const result = await navigationCoordinator.prepareToLeave(sourceId);
    if (!result.ok) {
      throw new Error(
        `${result.section ? `${result.section}: ` : ''}` +
        (result.message || 'Unsaved documentation prevents this action.')
      );
    }
    if (String(activeNoteIdRef.current) !== String(sourceId)) {
      throw new Error('The active clinical note changed while changes were being saved.');
    }
  }, [navigationCoordinator]);

  /** Navigate only after a successful load or explicitly confirmed Draft creation. */
  const handleVisitNavigate = useCallback(async (visit) => {
    const sourceId = activeNoteIdRef.current;
    if (!visit || visitNavigationBusy || typeChangeBusy || !sourceId) return;
    if (visit.clinicalNoteId != null &&
        String(visit.clinicalNoteId) === String(sourceId)) return;

    setVisitNavigationBusy(true);
    setPageError('');
    try {
      await prepareCurrentNoteToLeave(sourceId);
      const expectedEpisodeId = getNoteHeader(detail)?.clinicalEpisodeId ?? detail?.clinicalEpisodeId;
      if (!expectedEpisodeId) throw new Error('The active clinical episode could not be identified.');

      const resolved = await clinicalNoteService.resolveEpisodeVisitNote(
        { ...visit, clinicalEpisodeId: expectedEpisodeId },
        {
          confirmCreate: async (targetVisit) => window.confirm(
            `There is no Clinical Note for ${formatClinicalDate(targetVisit.noteDate)}.\n\n` +
            'Create a new Draft Clinical Note for this superbill?'
          ),
        }
      );
      if (resolved.cancelled) return;

      const targetId = resolved.note?.clinicalNoteId ??
        resolved.note?.note?.clinicalNoteId ??
        resolved.note?.clinicalNote?.clinicalNoteId;
      if (!targetId) throw new Error('The destination note ID was not returned.');
      if (String(activeNoteIdRef.current) !== String(sourceId)) {
        throw new Error('The active note changed during visit navigation.');
      }

      if (clinicalNoteIdProp != null && !params.clinicalNoteId && !params.id) {
        setSelectedNoteId(targetId);
      } else {
        const currentRouteId = params.clinicalNoteId ?? params.id;
        const path = location.pathname;
        const suffix = currentRouteId ? String(currentRouteId) : String(sourceId);
        const nextPath = path.endsWith(`/${suffix}`)
          ? `${path.slice(0, -suffix.length)}${targetId}`
          : `/clinical-notes/${targetId}`;
        navigate(`${nextPath}${location.search || ''}`);
      }
    } catch (error) {
      setPageError(error?.message || 'Unable to switch clinical visits.');
    } finally {
      setVisitNavigationBusy(false);
    }
  }, [clinicalNoteIdProp, detail, location.pathname, location.search,
    navigate, params.clinicalNoteId, params.id, prepareCurrentNoteToLeave,
    visitNavigationBusy, typeChangeBusy]);

  /**
   * Change the type of an existing Draft. Do not replace the reviewed version
   * with a newer one until all section autosaves have completed.
   */
  const handleNoteTypeChange = useCallback(async (event) => {
    const nextType = event.target.value;
    const sourceId = activeNoteIdRef.current;
    const currentNote = getNoteHeader(detail);
    const previousType = currentNote?.noteType ?? detail?.noteType;
    if (!nextType || nextType === previousType || typeChangeBusy || visitNavigationBusy) return;

    if (!window.confirm(
      `Change this Draft from ${getDocumentTitle(previousType)} to ${getDocumentTitle(nextType)}?\n\n` +
      'Existing documentation will be preserved, but sections and validation requirements will change.'
    )) return;

    setTypeChangeBusy(true);
    setPageError('');
    try {
      await prepareCurrentNoteToLeave(sourceId);
      // Autosaves may have advanced the parent version; fetch it after flushing.
      const fresh = await clinicalNoteService.getClinicalNote(sourceId);
      if (fresh?.status !== 'DRAFT' || fresh?.locked) {
        throw new Error('The note is no longer an editable Draft. Refresh before continuing.');
      }
      if (fresh?.noteType !== previousType) {
        throw new Error('Another user changed the documentation type. Refresh before continuing.');
      }
      if (fresh?.entityVersion == null) {
        throw new Error('The clinical note version could not be determined.');
      }
      await clinicalNoteService.changeNoteType(sourceId, {
        noteType: nextType,
        expectedVersion: fresh.entityVersion,
      });
      const [updatedDetail, updatedSignature] = await Promise.all([
        clinicalNoteService.getClinicalNoteDetail(sourceId),
        clinicalNoteService.getSignature(sourceId),
      ]);
      if (String(activeNoteIdRef.current) !== String(sourceId)) return;
      setDetail(updatedDetail);
      setSignature(updatedSignature || null);
      triggerPanelRefresh();
      void refreshPageSectionStatuses();
      if (updatedDetail?.clinicalEpisodeId || getNoteHeader(updatedDetail)?.clinicalEpisodeId) {
        const episodeId = getNoteHeader(updatedDetail)?.clinicalEpisodeId ?? updatedDetail.clinicalEpisodeId;
        try {
          setEpisodeVisits(await clinicalNoteService.getEpisodeVisits(episodeId));
        } catch (visitError) {
          setVisitsError(visitError?.message || 'Visit list could not be refreshed.');
        }
      }
    } catch (error) {
      setPageError(error?.message || 'Unable to change the documentation type.');
    } finally {
      setTypeChangeBusy(false);
    }
  }, [detail, prepareCurrentNoteToLeave, refreshPageSectionStatuses,
    triggerPanelRefresh, typeChangeBusy, visitNavigationBusy]);



  const note =

    useMemo(

      () =>

        getNoteHeader(

          detail

        ),

      [detail]

    );



  const noteType =

    useMemo(

      () =>

        getNoteType(

          detail

        ),

      [detail]

    );



  const status =

    useMemo(

      () =>

        getNoteStatus(

          detail

        ),

      [detail]

    );



  const entityVersion =

    useMemo(

      () =>

        getEntityVersion(

          detail

        ),

      [detail]

    );



  const locked =

    useMemo(

      () =>

        isNoteLocked(

          detail

        ),

      [detail]

    );



  const documentTitle =

    getDocumentTitle(

      noteType

    );



  
  const progress = useMemo(
    () => calculateClinicalProgress(noteType, pageSectionStatuses),
    [noteType, pageSectionStatuses]
  );

  const signed =

    status === 'SIGNED' ||

    Boolean(signature);



  /*

   * Normal clinical editing occurs only while the note is DRAFT.

   *

   * READY_FOR_REVIEW is intentionally read-only until it is returned

   * to DRAFT.

   *

   * SIGNED/locked notes are permanently read-only through the normal

   * editing workflow.

   */

  const editorReadOnly =

    locked ||

    status !== 'DRAFT';



  /**

   * Update page-level note metadata whenever one of the child

   * components reports a newer ClinicalNoteMain version.

   *

   * This callback deliberately does NOT increment refreshToken.

   *

   * Several components invoke both:

   *

   * onSaved(...)

   * onVersionChange(...)

   *

   * Incrementing here would therefore cause duplicate reloads and

   * could also create a reload cycle for panels that themselves

   * publish a version after responding to refreshToken.

   */

  const handleVersionChange =

    useCallback(

      (

        newVersion,

        noteResponse

      ) => {

        if (

          noteResponse

        ) {

          setDetail(

            (currentDetail) =>

              mergeNoteResponse(

                currentDetail,

                {

                  ...noteResponse,



                  entityVersion:

                    newVersion ??

                    noteResponse

                      .entityVersion,

                }

              )

          );



          return;

        }



        if (

          newVersion ===

            null ||

          newVersion ===

            undefined

        ) {

          return;

        }



        setDetail(

          (currentDetail) =>

            mergeNoteResponse(

              currentDetail,

              {

                entityVersion:

                  newVersion,

              }

            )

        );

      },

      []

    );



  /**

   * Shared callback for normal section autosaves.

   *

   * The child owns its section response.

   * The parent only needs the refreshed ClinicalNoteResponse and to

   * notify warnings/validation/workflow that data changed.

   */

  const handleSectionSaved =

    useCallback(

      (

        sectionResponse,

        noteResponse

      ) => {

        if (noteResponse) {

          setDetail(

            (currentDetail) =>

              mergeNoteResponse(

                currentDetail,

                noteResponse

              )

          );

        }



        triggerPanelRefresh();

        void refreshPageSectionStatuses();



        /*

         * Section state remains owned by the section component.

         */

        void sectionResponse;

      },

      [triggerPanelRefresh, refreshPageSectionStatuses]

    );



  /**

   * A warning acknowledgement changes both warning state and the

   * ClinicalNoteMain optimistic-lock version.

   *

   * The Warning panel itself supplies the expectedVersion it actually

   * reviewed. The page never substitutes entityVersion here.

   */

  const handleWarningAcknowledged =

    useCallback(

      (

        acknowledgementResponse,

        refreshedWarnings,

        refreshedNote

      ) => {

        if (refreshedNote) {

          setDetail(

            (currentDetail) =>

              mergeNoteResponse(

                currentDetail,

                refreshedNote

              )

          );

        }



        triggerPanelRefresh();
        void refreshPageSectionStatuses();

        void acknowledgementResponse;

        void refreshedWarnings;

      },

      [triggerPanelRefresh, refreshPageSectionStatuses]

    );



  /**

   * Workflow transition completed.

   *

   * This covers:

   *

   * DRAFT -> READY_FOR_REVIEW

   *

   * READY_FOR_REVIEW -> DRAFT

   *

   * The Workflow panel retains the version it validated/reviewed and

   * sends that exact expectedVersion to the backend.

   */

  const handleWorkflowChanged =

    useCallback(

      (

        workflowResponse,

        workflowInfo

      ) => {

        if (workflowResponse) {

          setDetail(

            (currentDetail) =>

              mergeNoteResponse(

                currentDetail,

                workflowResponse

              )

          );

        }



        triggerPanelRefresh();
        void refreshPageSectionStatuses();



        void workflowInfo;

      },

      [triggerPanelRefresh, refreshPageSectionStatuses]

    );



  /**

   * Validation is owned and rendered by the validation/workflow

   * components.

   *

   * No parent mutation is needed merely because validation ran.

   */

  const handleValidationChange =

    useCallback(

      (

        validationResponse

      ) => {

        void validationResponse;

      },

      []

    );



  /**

   * Permanent signing completed.

   *

   * Signing is irreversible through the normal workflow. The

   * Signature panel sends the exact reviewed expectedVersion and the

   * backend permanently transitions the note to:

   *

   * status = SIGNED

   * locked = true

   */

  const handleSigned =

    useCallback(

      (

        finalSignature,

        finalNote

      ) => {

        if (finalSignature) {

          setSignature(

            finalSignature

          );

        }



        if (finalNote) {

          setDetail(

            (currentDetail) =>

              mergeNoteResponse(

                currentDetail,

                finalNote

              )

          );

        }



        triggerPanelRefresh();
        void refreshPageSectionStatuses();

      },

      [triggerPanelRefresh, refreshPageSectionStatuses]

    );



  /**

   * Signature panel also reports the safe signature snapshot whenever

   * it loads/refetches it.

   */

  const handleSignatureChange =

    useCallback(

      (

        safeSignature

      ) => {

        setSignature(

          safeSignature ||

            null

        );

      },

      []

    );



  /**

   * Open the finalized signed HTML document in another tab.

   */

  async function handlePreview() {

    if (

      activeDocumentAction

    ) {

      return;

    }



    setActiveDocumentAction(

      'preview'

    );



    setPageError('');



    try {

      const html =

        await clinicalNoteService

          .getFinalPreviewHtml(

            clinicalNoteId

          );



      const blob =

        new Blob(

          [html],

          {

            type:

              'text/html;charset=utf-8',

          }

        );



      const objectUrl =

        URL.createObjectURL(

          blob

        );



      const previewWindow =

        window.open(

          objectUrl,

          '_blank',

          'noopener,noreferrer'

        );



      if (!previewWindow) {

        URL.revokeObjectURL(

          objectUrl

        );



        throw new Error(

          'The browser blocked the clinical-note preview window.'

        );

      }



      /*

       * Keep the temporary Blob URL alive long enough for the new tab

       * to finish loading the document.

       */

      window.setTimeout(

        () => {

          URL.revokeObjectURL(

            objectUrl

          );

        },

        60_000

      );

    } catch (error) {

      setPageError(

        error?.message ||

          'The finalized clinical-note preview could not be opened.'

      );

    } finally {

      setActiveDocumentAction('');

    }

  }



  /**

   * Download the finalized insurer-facing PDF.

   */

  async function handleDownloadPdf() {

    if (

      activeDocumentAction

    ) {

      return;

    }



    setActiveDocumentAction(

      'pdf'

    );



    setPageError('');



    try {

      await clinicalNoteService

        .downloadPdf(

          clinicalNoteId

        );

    } catch (error) {

      setPageError(

        error?.message ||

          'The clinical-note PDF could not be downloaded.'

      );

    } finally {

      setActiveDocumentAction('');

    }

  }



  if (loading || (detail && String(getNoteHeader(detail)?.clinicalNoteId ?? getNoteHeader(detail)?.id ?? clinicalNoteId) !== String(clinicalNoteId))) {

    return (

      <main

        style={{

          padding: 24,

          maxWidth: 1400,

          margin: '0 auto',

        }}

      >

        <p>

          Loading clinical note…

        </p>

      </main>

    );

  }



  if (

    pageError &&

    !detail

  ) {

    return (

      <main

        style={{

          padding: 24,

          maxWidth: 1400,

          margin: '0 auto',

        }}

      >

        <div

          role="alert"

          style={{

            border:

              '1px solid #c62828',

            background:

              '#fff5f5',

            padding: 16,

            borderRadius: 8,

          }}

        >

          <strong>

            Unable to load clinical

            note

          </strong>



          <p

            style={{

              marginBottom: 0,

            }}

          >

            {pageError}

          </p>

        </div>

      </main>

    );

  }



  return (

    <main className="clinical-note-workspace">

      {/* =====================================================

          PAGE HEADER

          ===================================================== */}

      <header
        className="clinical-note-workspace-header"

        style={{

          display: 'flex',

          gap: 16,

          justifyContent:

            'space-between',

          alignItems:

            'flex-start',

          flexWrap: 'wrap',

          marginBottom: 18,

        }}

      >

        <div>

          <div

            style={{

              fontSize: 13,

              color: '#666',

              marginBottom: 4,

            }}

          >

            Clinical Documentation

          </div>



          <h1

            style={{

              margin:

                '0 0 6px 0',

              fontSize: 26,

            }}

          >

            {documentTitle}

          </h1>



          <div

            style={{

              display: 'flex',

              gap: 8,

              flexWrap: 'wrap',

              alignItems: 'center',

            }}

          >

            <span

              style={{

                display:

                  'inline-block',

                padding:

                  '4px 9px',

                borderRadius: 999,



                background:

                  status ===

                  'SIGNED'

                    ? '#e8f5e9'

                    : status ===

                        'READY_FOR_REVIEW'

                      ? '#fff8e1'

                      : '#eef3f8',



                fontSize: 13,

                fontWeight: 700,

              }}

            >

              {formatEnum(

                status

              )}

            </span>



            {locked && (

              <span

                style={{

                  fontSize: 13,

                  fontWeight: 700,

                  color: '#555',

                }}

              >

                Locked

              </span>

            )}

          </div>

        </div>



        <div className="clinical-note-workspace-header__right">

          <div className="clinical-note-workspace-header__buttons">
          <button
            type="button"
            onClick={() =>
              loadClinicalNote(

                false,

                true

              )

            }

            disabled={

              refreshing ||

              Boolean(

                activeDocumentAction

              )

            }

          >

            {refreshing

              ? 'Refreshing…'

              : 'Refresh'}

          </button>



          {signed && (

            <>

              <button

                type="button"

                onClick={

                  handlePreview

                }

                disabled={Boolean(

                  activeDocumentAction

                )}

              >

                {activeDocumentAction ===

                'preview'

                  ? 'Opening…'

                  : 'Preview Final Note'}

              </button>



              <button

                type="button"

                onClick={

                  handleDownloadPdf

                }

                disabled={Boolean(

                  activeDocumentAction

                )}

              >

                {activeDocumentAction ===

                'pdf'

                  ? 'Preparing PDF…'

                  : 'Download PDF'}

              </button>

            </>

          )}

                  </div>

</div>

      </header>



      {pageError && (

        <div

          role="alert"

          style={{

            border:

              '1px solid #c62828',

            background:

              '#fff5f5',

            padding: 12,

            borderRadius: 8,

            marginBottom: 16,

          }}

        >

          {pageError}

        </div>

      )}



      {/* =====================================================

          WORKFLOW NOTICE

          ===================================================== */}

      {status ===

        'READY_FOR_REVIEW' && (

        <div

          style={{

            border:

              '1px solid #d6a100',

            background:

              '#fff8df',

            borderRadius: 8,

            padding: 12,

            marginBottom: 16,

          }}

        >

          <strong>

            Review mode

          </strong>



          <div

            style={{

              marginTop: 4,

              fontSize: 14,

            }}

          >

            This note is awaiting

            signature and is

            read-only. Return it to

            Draft before making

            clinical changes.

          </div>

        </div>

      )}



      {signed && (

        <div

          style={{

            border:

              '1px solid #89b88d',

            background:

              '#f2fbf3',

            borderRadius: 8,

            padding: 12,

            marginBottom: 16,

          }}

        >

          <strong>

            Permanent signed record

          </strong>



          <div

            style={{

              marginTop: 4,

              fontSize: 14,

            }}

          >

            Normal editing is

            disabled. Use Preview

            Final Note or Download

            PDF to view the

            insurer-facing document.

          </div>

        </div>

      )}



      <ClinicalNoteVisitNavigator
        visits={episodeVisits}
        clinicalNoteId={clinicalNoteId}
        onNavigate={handleVisitNavigate}
        disabled={visitNavigationBusy || typeChangeBusy || refreshing || Boolean(activeDocumentAction)}
      />
      {visitsError && (
        <div role="alert" style={{ marginBottom: 12, fontSize: 13, color: '#9a6700' }}>
          Visit navigation unavailable: {visitsError}
        </div>
      )}

      {!signed && progress.total > 0 && (progress.incomplete > 0 || progress.review > 0) && (
        <div className="clinical-note-attention-summary" role="status">
          <strong>Documentation to review:</strong>
          {progress.incomplete > 0 && <span>{progress.incomplete} section(s) with required issues</span>}
          {progress.review > 0 && <span>{progress.review} section(s) with warnings</span>}
          <a href="#clinical-note-review-anchor">Review validation and warnings</a>
        </div>
      )}

      {/* =====================================================

          NOTE METADATA

          ===================================================== */}

      <section
        className="clinical-note-visit-card"

        style={{

          border:

            '1px solid #ddd',

          borderRadius: 10,

          padding: 16,

          marginBottom: 16,

          background: '#fff',

        }}

      >

        <h2

          style={{

            fontSize: 17,

            margin:

              '0 0 14px 0',

          }}

        >

          Visit Information

        </h2>

        <div
          className="clinical-note-visit-grid"

          style={{

            display: 'flex',

            flexWrap: 'wrap',

            gap: 18,

          }}

        >

          <MetadataItem

            label="Patient"

            value={

              note.patientName ??

              detail?.patientName ??

              '—'

            }

          />

          <MetadataItem

            label="DOB"

            value={formatClinicalDate(

              note.patientDateOfBirth ??

                detail?.patientDateOfBirth

            )}

          />

          <MetadataItem

            label="Provider"

            value={

              note.providerName ??

              detail?.providerName ??

              '—'

            }

          />

          <MetadataItem

            label="Date of Service"

            value={formatClinicalDate(

              note.noteDate ??

                detail?.noteDate

            )}

          />

          <div className="clinical-note-document-type-field">
            <label htmlFor="clinical-note-document-type">Documentation Type</label>
            {status === 'DRAFT' && !locked && noteType !== 'INITIAL_EVALUATION' &&
             ['ADMIN', 'MANAGER', 'DOCTOR'].includes(String(userRole).replace(/^ROLE_/, '').toUpperCase()) ? (
              <select
                id="clinical-note-document-type"
                value={noteType || ''}
                onChange={handleNoteTypeChange}
                disabled={typeChangeBusy || visitNavigationBusy || refreshing || Boolean(activeDocumentAction)}
              >
                <option value="DAILY">Daily Note</option>
                <option value="PROGRESS_NOTE">Progress Note</option>
                <option value="RE_EVALUATION">Re-Evaluation</option>
                <option value="DISCHARGE">Discharge Note</option>
              </select>
            ) : (
              <span>{formatEnum(noteType)}</span>
            )}
            {typeChangeBusy && <small role="status">Changing documentation type…</small>}
          </div>

          <MetadataItem

            label="Status"

            value={formatEnum(

              status

            )}

          />

          <MetadataItem

            label="Body Part"

            value={

              note.bodyPartDisplayName ??

              detail?.bodyPartDisplayName ??

              '—'

            }

          />

          <MetadataItem

            label="Laterality"

            value={formatEnum(

              note.laterality ??

                detail?.laterality

            )}

          />

          <MetadataItem

            label="Authorization"

            value={

              note.authorizationCode ??

              detail?.authorizationCode ??

              '—'

            }

          />

          <MetadataItem

            label="Diagnosis"

            wide

            value={

              Array.isArray(

                note.diagnoses ??

                  detail?.diagnoses

              ) &&

              (

                note.diagnoses ??

                detail?.diagnoses

              ).length > 0

                ? (

                    <div

                      style={{

                        display: 'grid',

                        gap: 4,

                      }}

                    >

                      {(

                        note.diagnoses ??

                        detail?.diagnoses

                      ).map(

                        (diagnosis, index) => (

                          <div

                            key={`${diagnosis}-${index}`}

                          >

                            {diagnosis}

                          </div>

                        )

                      )}

                    </div>

                  )

                : '—'

            }

          />

        </div>

      </section>



      {/* =====================================================

          SIGNATURE SUMMARY

          ===================================================== */}

      {signature && (

        <section

          style={{

            border:

              '1px solid #ddd',

            borderRadius: 10,

            padding: 16,

            marginBottom: 16,

            background: '#fff',

          }}

        >

          <h2

            style={{

              fontSize: 17,

              margin:

                '0 0 14px 0',

            }}

          >

            Permanent Signature

          </h2>



          <div

            style={{

              display: 'flex',

              flexWrap: 'wrap',

              gap: 18,

            }}

          >

            <MetadataItem

              label="Provider"

              value={

                signature.providerName

              }

            />



            <MetadataItem

              label="Credentials"

              value={

                signature.credentials

              }

            />



            <MetadataItem

              label="License"

              value={

                [

                  signature

                    .licenseState,

                  signature

                    .licenseNumber,

                ]

                  .filter(Boolean)

                  .join(' ') ||

                '—'

              }

            />



            <MetadataItem

              label="Signature Date"

              value={formatClinicalDate(

                signature.signatureDate

              )}

            />



            <MetadataItem

              label="Signature Image"

              value={

                signature

                  .signatureImageAvailable

                  ? 'Available'

                  : 'Unavailable'

              }

            />

          </div>

        </section>

      )}



      <div className="clinical-note-editor-grid">
        <div className="clinical-note-editor-main">


      {/* =====================================================

          CLINICAL DOCUMENTATION WORKSPACE

          The backend note type decides which wrapper is rendered.
          Each wrapper owns the exact editable sections for that form,
          while each individual section still owns loading/autosave,
          validation, warnings, and optimistic-lock coordination.

          ===================================================== */}

      <section
        className="clinical-note-documentation-panel"

        style={{

          border:

            '1px solid #ddd',

          borderRadius: 10,

          background: '#fff',

          overflow: 'hidden',

          marginBottom: 16,

        }}

      >

        <div

          style={{

            padding:

              '14px 16px',

            borderBottom:

              '1px solid #ddd',

            background:

              '#f7f8fa',

          }}

        >

          <h2

            style={{

              fontSize: 17,

              margin: 0,

            }}

          >

            Clinical Documentation

          </h2>

        </div>



        <div
          className="clinical-note-documentation-panel__body"

          style={{

            padding: 16,

          }}

        >

          {(noteType ===

            'INITIAL_EVALUATION' ||

            noteType ===

              'RE_EVALUATION') && (

            <ClinicalNoteEvaluationForm

              navigationCoordinator={navigationCoordinator}
              key={`${clinicalNoteId}-${noteType}`} clinicalNoteId={

                clinicalNoteId

              }

              disabled={

                editorReadOnly

              }

              onSaved={

                handleSectionSaved

              }

              onVersionChange={

                handleVersionChange

              }

            />

          )}



          {noteType === 'DAILY' && (

            <ClinicalNoteDailyForm

              navigationCoordinator={navigationCoordinator}
              key={`${clinicalNoteId}-${noteType}`} clinicalNoteId={

                clinicalNoteId

              }

              disabled={

                editorReadOnly

              }

              onSaved={

                handleSectionSaved

              }

              onVersionChange={

                handleVersionChange

              }

            />

          )}



          {noteType ===

            'PROGRESS_NOTE' && (

            <ClinicalNoteProgressForm

              navigationCoordinator={navigationCoordinator}
              key={`${clinicalNoteId}-${noteType}`} clinicalNoteId={

                clinicalNoteId

              }

              disabled={

                editorReadOnly

              }

              onSaved={

                handleSectionSaved

              }

              onVersionChange={

                handleVersionChange

              }

            />

          )}



          {noteType ===

            'DISCHARGE' && (

            <ClinicalNoteDischargeForm

              navigationCoordinator={navigationCoordinator}
              key={`${clinicalNoteId}-${noteType}`} clinicalNoteId={

                clinicalNoteId

              }

              disabled={

                editorReadOnly

              }

              onSaved={

                handleSectionSaved

              }

              onVersionChange={

                handleVersionChange

              }

            />

          )}



          {noteType &&

            ![

              'INITIAL_EVALUATION',

              'RE_EVALUATION',

              'DAILY',

              'PROGRESS_NOTE',

              'DISCHARGE',

            ].includes(noteType) && (

              <div

                role="alert"

                style={{

                  border:

                    '1px solid #c62828',

                  background:

                    '#fff5f5',

                  borderRadius: 8,

                  padding: 12,

                }}

              >

                Unsupported clinical note type:{' '}

                {String(noteType)}

              </div>

            )}

        </div>

      </section>




          {/* Submit/review workflow lives directly beneath documentation. */}
          {!signed && (
            <details
              className="clinical-note-workspace-disclosure clinical-note-workflow-disclosure"
              defaultOpen={status === 'DRAFT'}
              key={`${clinicalNoteId}-workflow`}
            >
              <summary className="clinical-note-workspace-disclosure__summary">
                <span>Submit / Clinical Note Workflow</span>
                <span className="clinical-note-workspace-disclosure__hint">
                  {status === 'READY_FOR_REVIEW' ? 'In review' : 'Draft and review actions'}
                </span>
              </summary>
              <div className="clinical-note-workspace-disclosure__body">
<ClinicalNoteWorkflowPanel

              clinicalNoteId={

                clinicalNoteId

              }

              enabled

              disabled={

                locked

              }

              refreshToken={

                refreshToken

              }

              onWorkflowChanged={

                handleWorkflowChanged

              }

              onValidationChange={

                handleValidationChange

              }

              onVersionChange={

                handleVersionChange

              }

            />
              </div>
            </details>
          )}


      {/* =====================================================

          PERMANENT SIGNING



          The panel is available in READY_FOR_REVIEW and remains

          available after signing so the permanent safe signature

          representation can still be displayed.



          Signing itself is disabled unless the note is actually in

          READY_FOR_REVIEW and is not locked.

          ===================================================== */}

      {(status ===

        'READY_FOR_REVIEW' ||

        signed) && (

        <section

          style={{

            border:

              '1px solid #ddd',

            borderRadius: 10,

            background: '#fff',

            overflow: 'hidden',

            marginBottom: 16,

          }}

        >

          <div

            style={{

              padding:

                '14px 16px',

              borderBottom:

                '1px solid #ddd',

              background:

                '#f7f8fa',

            }}

          >

            <h2

              style={{

                fontSize: 17,

                margin: 0,

              }}

            >

              Provider Signature

            </h2>

          </div>



          <div

            style={{

              padding: 16,

            }}

          >

            <ClinicalNoteSignaturePanel

              clinicalNoteId={

                clinicalNoteId

              }

              enabled

              disabled={

                locked ||

                status !==

                  'READY_FOR_REVIEW'

              }

              userRole={

                userRole

              }

              refreshToken={

                refreshToken

              }

              onSigned={

                handleSigned

              }

              onSignatureChange={

                handleSignatureChange

              }

              onVersionChange={

                handleVersionChange

              }

            />

          </div>

        </section>

      )}



      {/* =====================================================

          FINAL INSURER-FACING DOCUMENT



          This component intentionally uses the backend final renderer

          as the source of truth. It appears only after permanent

          signing.

          ===================================================== */}

      {signed && (

        <section

          style={{

            border:

              '1px solid #ddd',

            borderRadius: 10,

            background: '#fff',

            overflow: 'hidden',

            marginBottom: 16,

          }}

        >

          <div

            style={{

              padding:

                '14px 16px',

              borderBottom:

                '1px solid #ddd',

              background:

                '#f7f8fa',

            }}

          >

            <h2

              style={{

                fontSize: 17,

                margin: 0,

              }}

            >

              Final Clinical Document

            </h2>

          </div>



          <div

            style={{

              padding: 16,

            }}

          >

            <ClinicalNoteFinalDocumentPanel

              clinicalNoteId={

                clinicalNoteId

              }

              enabled

              refreshToken={

                refreshToken

              }

              onVersionChange={

                handleVersionChange

              }

            />

          </div>

        </section>

      )}


      {/* PDF packet controls remain mounted when the disclosure is closed. */}
      <details className="clinical-note-workspace-disclosure clinical-note-pdf-disclosure" defaultOpen={false}>
        <summary className="clinical-note-workspace-disclosure__summary">
          <span>PDF Packets</span>
          <span className="clinical-note-workspace-disclosure__hint">Expand to view or generate packets</span>
        </summary>
        <div className="clinical-note-workspace-disclosure__body">
          <ClinicalNotePdfPacketPanel
            clinicalNoteId={clinicalNoteId}
            authId={note.authId ?? detail?.authId}
            clinicalEpisodeId={note.clinicalEpisodeId ?? detail?.clinicalEpisodeId}
            signed={signed}
          />
        </div>
      </details>

        </div>

        <aside className="clinical-note-review-sidebar" id="clinical-note-review-anchor" aria-label="Section status and clinical validation">
          <section className="clinical-note-status-panel">
            <ClinicalProgressCard
              progress={progress}
              noteType={noteType}
              statuses={pageSectionStatuses}
            />
          </section>

          {!signed && (
            <>
              <details className="clinical-note-workspace-disclosure" defaultOpen={false}>
                <summary className="clinical-note-workspace-disclosure__summary">
                  <span>Clinical Warnings</span>
                  <span className="clinical-note-workspace-disclosure__hint">Expand to review warnings</span>
                </summary>
                <div className="clinical-note-workspace-disclosure__body">
<ClinicalNoteWarningPanel

              clinicalNoteId={

                clinicalNoteId

              }

              enabled

              disabled={

                editorReadOnly

              }

              refreshToken={

                refreshToken

              }

              onAcknowledged={

                handleWarningAcknowledged

              }

              onVersionChange={

                handleVersionChange

              }

            />
                </div>
              </details>

              <details className="clinical-note-workspace-disclosure" defaultOpen={false}>
                <summary className="clinical-note-workspace-disclosure__summary">
                  <span>Clinical Validation</span>
                  <span className="clinical-note-workspace-disclosure__hint">Expand for final checks</span>
                </summary>
                <div className="clinical-note-workspace-disclosure__body">
<ClinicalNoteValidationPanel

              clinicalNoteId={

                clinicalNoteId

              }

              enabled

              refreshToken={

                refreshToken

              }

              onValidation={

                handleValidationChange

              }

              onVersionChange={

                handleVersionChange

              }

            />
                </div>
              </details>
            </>
          )}
        </aside>
      </div>

    </main>

  );

}